import isEqual from 'lodash/isEqual';

/**
 * A projection of a keyed subject that hands back the PREVIOUS result reference for as long as the
 * projected value is unchanged: a freshly projected result that is deep-equal to the one last handed
 * out for the same key is dropped in favour of that earlier reference. Every key keeps its own
 * previous result, so any number of subjects can be read in turn without one displacing another's,
 * and a source object already projected answers from its cached result without projecting it again.
 *
 * Use it for selectors that build an object out of inputs which get re-created with equal content —
 * a slice re-mapped from a new response, one of several stored versions of the same entity — where a
 * new reference per recomputation would re-render every subscriber, recompute every downstream
 * memoized selector and re-run every effect keyed on the object, all for a value that did not
 * change. Sagas and effects keyed on object identity are the readers that cannot defend themselves;
 * RTK Query is unaffected either way, as its cache key comes from serialized arguments.
 *
 * Sources are held weakly, so a source object the store has dropped is not kept alive here; per key
 * only the projected result is retained, for as long as the projection itself lives.
 */
export function createValueStableProjection<TKey, TSource extends object | null | undefined, TResult>(
    project: (source: TSource) => TResult,
): (key: TKey, source: TSource) => TResult {
    const resultBySource = new WeakMap<object, { result: TResult }>();
    const previousResultByKey = new Map<TKey, { result: TResult }>();

    return (key, source) => {
        // A source already projected skips the projection, but still goes through the key's previous
        // result: read again after the key has moved to an equal-valued source, its cached result is
        // the stale reference of the two, and handing it back would break the value stability.
        const cached = source ? resultBySource.get(source) : undefined;
        const projected = cached ? cached.result : project(source);
        const previous = previousResultByKey.get(key);
        const result = previous && isEqual(previous.result, projected) ? previous.result : projected;

        if (!previous || previous.result !== result) {
            previousResultByKey.set(key, { result });
        }
        if (source && (!cached || cached.result !== result)) {
            resultBySource.set(source, { result });
        }

        return result;
    };
}
